
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { ReportData } from '@/types/report';
import { ReportHeader } from '@/components/reports/ReportHeader';
import { ClassInfoSection } from '@/components/reports/ClassInfoSection';
import { ReportContentSection } from '@/components/reports/ReportContentSection';
import { ReportDetailsTable } from '@/components/reports/ReportDetailsTable';
import { formatDate, formatTime, generateReportText } from '@/utils/reportFormatters';
import { useAuth } from '@/context/auth/AuthContext';
import { ReportUpdateLogs } from '@/components/admin/ReportUpdateLogs';
import { ReportEditButton } from '@/components/admin/ReportEditButton';
import { useQuery } from '@tanstack/react-query';

const ReportDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const { toast } = useToast();
  const { user, checkAdminStatus } = useAuth();

  const { 
    data: report, 
    isLoading, 
    refetch 
  } = useQuery({
    queryKey: ['report-detail', id],
    queryFn: async () => {
      if (!id) throw new Error('No report ID provided');
      
      const { data, error } = await supabase
        .from('student_development_reports')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as ReportData;
    },
  });

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      if (user) {
        const isUserAdmin = await checkAdminStatus();
        setIsAdmin(isUserAdmin);
      }
    };
    
    checkAdmin();
  }, [user, checkAdminStatus]);

  const handleReportUpdated = () => {
    refetch();
    toast({
      title: 'รายงานถูกอัพเดทแล้ว',
      description: 'ข้อมูลรายงานได้รับการอัพเดท',
    });
  };

  const reportText = report ? generateReportText(report) : '';

  if (isLoading) {
    return (
      <div className="w-full px-4 py-8 flex justify-center">
        <div className="animate-pulse text-gray-500">กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8">
      {report ? (
        <Card className="shadow-lg">
          <ReportHeader 
            report={report} 
            formatDate={formatDate} 
            adminControls={isAdmin ? (
              <ReportEditButton reportId={report.id} onSuccess={handleReportUpdated} />
            ) : undefined}
          />
          <CardContent className="p-6">
            <ClassInfoSection 
              report={report}
              formatDate={formatDate}
              formatTime={formatTime}
            />
            <ReportContentSection reportText={reportText} report={report} />
            <ReportDetailsTable report={report} />
            
            {isAdmin && id && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">ประวัติการแก้ไข</h3>
                <ReportUpdateLogs reportId={id} />
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="shadow-lg">
          <CardContent className="p-6 text-center">
            <p className="text-gray-500 py-8">ไม่พบข้อมูลรายงาน</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportDetail;
